<template>
  <div>
    <b-sidebar
      id="sidebar-edit-contract"
      sidebar-class="sidebar-lg"
      :visible="editContractSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @change="(val) => mutateEditContractSidebar({ visible: val })"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Editar contrato {{ editContractSidebar.id }}</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <b-alert :show="statusOnly" variant="warning" class="p-1">
            Competência fechada: só é possível trocar entre status válidos. As datas
            exigem alteração extraordinária.
          </b-alert>
          <b-row>
            <b-col>
              <b-form-group
                label="Proposta"
                label-for="contract-edit-proposal_number"
              >
                <b-form-input
                  id="contract-edit-proposal_number"
                  type="text"
                  v-model="proposal_number"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group label="Status" label-for="contract-edit-status">
                <v-select
                  id="contract-edit-status"
                  v-model="id_contract_status"
                  required
                  :reduce="(status_list) => status_list.id"
                  :options="selectableStatus"
                  label="name"
                  :loading="loadingStatuses"
                  @keyup.enter="search"
                  :class="getSelectErrorClass(v$.id_contract_status.$error)"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.id_contract_status.$error">
                    Você deve informar um status
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Data da criação"
                label-for="contract-created-at"
              >
                <b-form-input
                  id="contract-created-ate"
                  :disabled="statusOnly"
                  v-model="createdAt"
                  type="date"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Data da assinatura"
                label-for="contract-signed-at"
              >
                <b-form-input
                  id="contract-signed-at"
                  :disabled="statusOnly"
                  v-model="signed_at"
                  type="date"
                />
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Data do pagamento automático"
                label-for="contract-paid-at"
              >
                <b-form-input
                  id="contract-paid-at"
                  :disabled="statusOnly"
                  v-model="paid_at"
                  type="date"
                  :class="{
                    'is-invalid': v$.paid_at.$error,
                  }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.paid_at.deployedAtInformed.$invalid">
                    A data de pagamento automático só pode ser informada na
                    ausência da data de implantação manual
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <b-form-group
                label="Data da implantação manual"
                label-for="contract-deployed-at"
              >
                <b-form-input
                  id="contract-deployed-at"
                  :disabled="statusOnly"
                  v-model="deployed_at"
                  type="date"
                  :class="{
                    'is-invalid': v$.deployed_at.$error,
                  }"
                />
                <div class="invalid-feedback">
                  <span v-if="v$.deployed_at.paidAtInformed.$invalid">
                    A data de implantação manual só pode ser informada na ausência da
                    data de pagamento automático
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col class="mt-1">
              <!-- Form Actions -->
              <div class="d-flex">
                <b-button
                  :disabled="saving"
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="mr-2"
                  type="submit"
                >
                  {{ saving ? "Salvando..." : "Salvar" }}
                </b-button>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import _ from "lodash";
import {
  BAlert,
  BSidebar,
  BForm,
  BFormGroup,
  BButton,
  BFormInput,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { getVueSelectErrorClass } from "@/helpers/validators";
import { apiErrorToast } from "@/helpers/error_messages";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import { NON_MUTABLE_STATUSES } from "@/constants/contract_status";

export default {
  components: {
    // BSV
    BAlert,
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      saving: false,
      loadingStatuses: false,
      proposal_number: undefined,
      id_contract_status: undefined,
      createdAt: undefined,
      signed_at: undefined,
      paid_at: undefined,
      deployed_at: undefined,
      // Preenchido apenas quando a edição foi autorizada por uma alteração
      // extraordinária: o backend exige o id da exceção para aceitar a escrita.
      closing_exception_id: undefined,
    };
  },
  watch: {
    editContractSidebar: {
      handler(val) {
        this.fillContractData(val);
      },
    },
  },
  validations() {
    return {
      proposal_number: {},
      id_contract_status: { required },
      createdAt: {},
      signed_at: {},
      paid_at: {
        deployedAtInformed: (value) => {
          if (this.deployed_at && value) {
            return false;
          }
          return true;
        },
      },
      deployed_at: {
        paidAtInformed: (value) => {
          if (this.paid_at && value) {
            return false;
          }
          return true;
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      editContractSidebar: types.EDIT_CONTRACT_SIDEBAR,
      status: types.STATUS_LIST,
      paymentGateways: sharedTypes.PAYMENT_GATEWAYS,
    }),
    // A exclusão de contratos só acontece pelo fluxo solicitar→aprovar (SUPORTE): nenhum
    // desses status pode ser escolhido direto no combo de status.
    selectableStatus() {
      return (this.status || []).filter(
        (status_item) =>
          !NON_MUTABLE_STATUSES.includes(Number(status_item.id))
          && this.isStatusAllowed(Number(status_item.id))
      );
    },
    // Competência fechada fora da alteração extraordinária: só a troca entre status válidos,
    // com as datas travadas. Quem decide é o servidor (`edit_mode`/`allowed_status_ids`).
    statusOnly() {
      return (
        this.editContractSidebar.edit_mode === "status_only"
        && !this.closing_exception_id
      );
    },
  },
  setup() {
    return { toast: useToast(), v$: useVuelidate() };
  },
  mounted() {
    this.loadingStatuses = true;
    this.getStatusList()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os status para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loadingStatuses = false;
      });
    if (!this.paymentGateways?.length) {
      this.getPaymentGateways();
    }
  },
  methods: {
    ...mapMutations({
      mutateEditContractSidebar: types.MUTATE_EDIT_CONTRACT_SIDEBAR,
    }),
    ...mapActions({
      updateContract: types.UPDATE_CONTRACT,
      getStatusList: types.GET_STATUS_LIST,
      getPaymentGateways: sharedTypes.GET_PAYMENT_GATEWAYS,
    }),
    fillContractData(data) {
      this.proposal_number = data.proposal_number;
      this.id_contract_status = data.id_contract_status;
      this.createdAt = data.createdAt;
      this.signed_at = data.signed_at;
      this.paid_at = data.paid_at;
      this.deployed_at = data.deployed_at;
      this.closing_exception_id = data.closing_exception_id;
    },
    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;
      this.saving = true;
      const {
        proposal_number,
        id_contract_status,
        createdAt,
        signed_at,
        paid_at,
        deployed_at,
        closing_exception_id,
      } = this;
      const { id } = this.editContractSidebar;
      this.updateContract({
        id,
        proposal_number,
        id_contract_status,
        createdAt,
        signed_at,
        paid_at,
        deployed_at,
        closing_exception_id,
      })
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso",
              text: "O contrato foi salvo!",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.mutateEditContractSidebar({ visible: false });
          this.editContractSidebar.saveAction(
            this.editContractSidebar.currentPage
          );
        })
        .catch((error) => {
          // Cobre 400 (mensagem em `error`), 422 (sacola de validação) e o 423 do
          // fechamento de competência, que traz o motivo em `message`.
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Ocorreu um erro ao salvar o contrato. Entre em contato com o setor de TI."
            ),
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    // O status atual continua na lista para o combo exibir o valor selecionado.
    isStatusAllowed(statusId) {
      if (!this.statusOnly) return true;
      const allowed = (this.editContractSidebar.allowed_status_ids || []).map(Number);
      return (
        allowed.includes(statusId)
        || statusId === Number(this.editContractSidebar.id_contract_status)
      );
    },
    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },
  },
};
</script>
